// src/components/KitchenDisplay/restaurantApi.js

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Base URL for your API endpoints
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 * It's crucial that a user is logged in for this to return a valid token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the API_BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `Restaurant API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    
    let response;
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        const corsError = new Error(
          `CORS Error: Unable to connect to the Restaurant API. ` +
          'This may be a preflight (OPTIONS) request issue or backend CORS configuration issue. ' +
          'The API Gateway needs to be configured to allow CORS from your origin.'
        );
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true;
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error('Restaurant API: CORS/Preflight Error Details:', {
          method,
          endpoint,
          url: `${API_BASE_URL}${endpoint}`,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers (Access-Control-Allow-Origin, Access-Control-Allow-Methods, Access-Control-Allow-Headers)',
        });
        throw corsError;
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Restaurant API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service (including 404s as user requested)
      loggingService.logError(
        'api_error_restaurant',
        `Restaurant API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[restaurantApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    // Re-throw CORS errors as-is
    if (error.isCorsError) {
      // Log CORS errors to logging service
      loggingService.logError(
        'api_error_restaurant',
        `Restaurant API CORS/network error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'CORS_ERROR',
        }
      ).catch(logError => {
        console.warn('[restaurantApi] Failed to log error:', logError);
      });
      throw error;
    }

    console.error(
      `Restaurant API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_restaurant',
      `Restaurant API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[restaurantApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Restaurant/Kitchen Management.
 */
export const restaurantApi = {
  /**
   * Fetches the current shift for a site.
   * GET /shift/current-for-site?site_id={siteId}
   * @param {string} siteId - The ID of the site.
   * @returns {Promise<Object>} - Response containing shift ID and details.
   * @throws {Error} - Throws an error if the API request fails.
   */
  fetchCurrentShiftForSite: async (siteId) => {
    if (!siteId) {
      throw new Error('Site ID is required');
    }
    const response = await makeApiRequest(
      `/shift/current-for-site?site_id=${siteId}`
    );
    return response.json();
  },

  /**
   * Fetches all active orders for the current shift.
   * GET /restaurant/order/get-active?ss_id={shiftId}
   * @param {string} shiftId - The shift schedule ID (ss_id).
   * @returns {Promise<Array>} - Array of active order objects.
   * @throws {Error} - Throws an error if the API request fails.
   */
  getActiveOrders: async (shiftId) => {
    if (!shiftId) {
      throw new Error('Shift ID (ss_id) is required');
    }
    const response = await makeApiRequest(
      `/restaurant/order/get-active?ss_id=${shiftId}`
    );
    return response.json();
  },

  /**
   * Fetches dish details by dish ID (cached).
   * GET /restaurant/dish?dish_id={dishId}
   * @param {string} dishId - The ID of the dish.
   * @returns {Promise<Object>} - Dish details object.
   * @throws {Error} - Throws an error if the API request fails.
   */
  getDish: async (dishId) => {
    if (!dishId) {
      throw new Error('Dish ID is required');
    }
    const response = await makeApiRequest(
      `/restaurant/dish?dish_id=${dishId}`
    );
    return response.json();
  },

  /**
   * Fetches list of active chefs for chef assignment dropdown.
   * GET /restaurant/kitchen/get-active-chefs?ss_id={shiftId}
   * @param {string} shiftId - The shift schedule ID (ss_id).
   * @returns {Promise<Array>} - Array of chef objects with user_id and user_name.
   * @throws {Error} - Throws an error if the API request fails.
   */
  getActiveChefs: async (shiftId) => {
    if (!shiftId) {
      throw new Error('Shift ID (ss_id) is required');
    }
    const response = await makeApiRequest(
      `/restaurant/kitchen/get-active-chefs?ss_id=${shiftId}`
    );
    return response.json();
  },

  /**
   * Updates order item stage (start/end operations).
   * POST /restaurant/kitchen/set-stage
   * @param {Object} payload - Request body containing:
   *   - order_item_id: string (required)
   *   - stage_name: string (required) - e.g., "queued", "in_progress", "ready", "served"
   *   - operation: "start" | "end" (required for non-READY stages)
   *   - chef_assigned: string (required when operation == "start" and stage_name != "queued")
   *   - chef_remarks: string (optional, used when operation == "end")
   * Note: For "ready" stage, only order_item_id and stage_name are required (no operation needed)
   * @returns {Promise<Object>} - API response.
   * @throws {Error} - Throws an error if the API request fails.
   */
  setOrderItemStage: async (payload) => {
    if (!payload.order_item_id || !payload.stage_name) {
      throw new Error('order_item_id and stage_name are required fields');
    }
    
    // For "ready" stage, operation is not required
    if (payload.stage_name.toLowerCase() !== 'ready' && !payload.operation) {
      throw new Error('operation is required for non-READY stages');
    }
    
    const response = await makeApiRequest(
      '/restaurant/kitchen/set-stage',
      'POST',
      payload
    );
    return response.json();
  },

  /**
   * Sends notification when order item is marked as ready.
   * POST /restaurant/send-ready-notification
   * @param {string} orderItemId - The order item ID.
   * @returns {Promise<Object>} - API response.
   * @throws {Error} - Throws an error if the API request fails.
   */
  sendReadyNotification: async (orderItemId) => {
    if (!orderItemId) {
      throw new Error('Order item ID is required');
    }
    const response = await makeApiRequest(
      '/restaurant/send-ready-notification',
      'POST',
      { order_item_id: orderItemId }
    );
    return response.json();
  },

  /**
   * Deletes a queued order item (KDS cancel action).
   * DELETE /restaurant/delete-order-item-if-status-queued
   * @param {string} orderId - The parent order ID.
   * @param {string} orderItemId - The order item ID to delete.
   * @returns {Promise<Object>} - API response.
   * @throws {Error} - Throws an error if the API request fails.
   */
  deleteQueuedOrderItem: async (orderId, orderItemId) => {
    if (!orderId || !orderItemId) {
      throw new Error('Both order_id and order_item_id are required');
    }
    const response = await makeApiRequest(
      '/restaurant/delete-order-item-if-status-queued',
      'DELETE',
      { order_id: orderId, order_item_id: orderItemId }
    );
    return response.json();
  },
};

